import { DealType } from "@/types/deal";

export interface DealFieldConfig {
  common: {
    title: string;
    type: string;
    value: string;
    jurisdiction: string;
    buyer: {
      label: string;
      placeholder: string;
      description: string;
    };
    seller: {
      label: string;
      placeholder: string;
      description: string;
    };
  };
  specific: {
    [key: string]: {
      label: string;
      placeholder: string;
      description?: string;
      type: 'text' | 'number' | 'select' | 'textarea' | 'date';
      options?: Array<{ value: string; label: string }>;
      required?: boolean;
      showCurrencyPrefix?: boolean;
    };
  };
}

export const DEAL_FIELD_CONFIGS: Record<DealType, DealFieldConfig> = {
  MA: {
    common: {
      title: "Deal Title",
      type: "Deal Type",
      value: "Deal Value",
      jurisdiction: "Jurisdiction",
      buyer: {
        label: "Buyer/Acquirer",
        placeholder: "e.g., Global Holdings Inc",
        description: "The acquiring entity or buyer"
      },
      seller: {
        label: "Seller/Target",
        placeholder: "e.g., TechCorp Ltd",
        description: "The target company or seller"
      }
    },
    specific: {
      dealStructure: {
        label: "Deal Structure",
        placeholder: "Select deal structure",
        description: "Type of M&A transaction",
        type: 'select',
        options: [
          { value: 'MERGER', label: 'Merger' },
          { value: 'ACQUISITION', label: 'Acquisition' },
          { value: 'ASSET_PURCHASE', label: 'Asset Purchase' },
          { value: 'SHARE_PURCHASE', label: 'Share Purchase' },
          { value: 'JOINT_VENTURE', label: 'Joint Venture' }
        ]
      },
      paymentType: {
        label: "Payment Type",
        placeholder: "Select payment type",
        description: "How the deal will be funded",
        type: 'select',
        options: [
          { value: 'CASH', label: 'Cash' },
          { value: 'STOCK', label: 'Stock' },
          { value: 'MIXED', label: 'Mixed (Cash + Stock)' }
        ]
      },
      synergiesExpected: {
        label: "Expected Synergies",
        placeholder: "e.g., Cost savings, market expansion, operational efficiencies",
        description: "Expected benefits from the transaction",
        type: 'textarea'
      }
    }
  },
  DEBT_FINANCING: {
    common: {
      title: "Deal Title",
      type: "Deal Type",
      value: "Loan Amount",
      jurisdiction: "Jurisdiction",
      buyer: {
        label: "Lender",
        placeholder: "e.g., Global Bank Inc",
        description: "The lending institution(s)"
      },
      seller: {
        label: "Borrower/Issuer",
        placeholder: "e.g., TechCorp Ltd",
        description: "The borrowing entity or bond issuer"
      }
    },
    specific: {
      debtType: {
        label: "Type of Debt",
        placeholder: "Select debt type",
        description: "Classification of the debt instrument",
        type: 'select',
        options: [
          { value: 'TERM_LOAN', label: 'Term Loan' },
          { value: 'REVOLVER', label: 'Revolving Credit Facility' },
          { value: 'BOND', label: 'Bond' },
          { value: 'CONVERTIBLE', label: 'Convertible Bond' },
          { value: 'SENIOR_DEBT', label: 'Senior Debt' },
          { value: 'SUBORDINATED_DEBT', label: 'Subordinated Debt' }
        ],
        required: false
      },
      interestRate: {
        label: "Interest Rate / Coupon (%)",
        placeholder: "e.g., 5.25",
        description: "Annual interest rate or coupon rate",
        type: 'number'
      },
      maturityDate: {
        label: "Maturity Date / Tenor",
        placeholder: "Select maturity date",
        description: "When the debt matures",
        type: 'date'
      },
      covenants: {
        label: "Covenants / Collateral",
        placeholder: "e.g., Maintain debt-to-equity ratio below 2:1, secured by company assets",
        description: "Key covenants and collateral securing the debt",
        type: 'textarea'
      }
    }
  },
  INVESTMENT: {
    common: {
      title: "Deal Title",
      type: "Deal Type",
      value: "Investment Amount",
      jurisdiction: "Jurisdiction",
      buyer: {
        label: "Investor(s)",
        placeholder: "e.g., Venture Capital Fund LP",
        description: "The investing entity or fund"
      },
      seller: {
        label: "Portfolio Company",
        placeholder: "e.g., TechStartup Inc",
        description: "The target company receiving investment"
      }
    },
    specific: {
      fundingRound: {
        label: "Funding Round",
        placeholder: "Select funding round",
        description: "Stage of investment",
        type: 'select',
        options: [
          { value: 'SEED', label: 'Seed' },
          { value: 'SERIES_A', label: 'Series A' },
          { value: 'SERIES_B', label: 'Series B' },
          { value: 'SERIES_C', label: 'Series C' },
          { value: 'SERIES_D', label: 'Series D+' },
          { value: 'BRIDGE', label: 'Bridge Round' },
          { value: 'GROWTH', label: 'Growth/Late Stage' },
          { value: 'PRE_IPO', label: 'Pre-IPO' }
        ],
        required: false
      },
      equityStake: {
        label: "Equity Stake (%)",
        placeholder: "e.g., 15.5",
        description: "Percentage ownership acquired",
        type: 'number'
      },
      preMoneyValuation: {
        label: "Pre-money Valuation",
        placeholder: "e.g., 50000000",
        description: "Company valuation before investment",
        type: 'number',
        showCurrencyPrefix: true
      },
      postMoneyValuation: {
        label: "Post-money Valuation",
        placeholder: "e.g., 60000000",
        description: "Company valuation after investment",
        type: 'number',
        showCurrencyPrefix: true
      },
      boardRights: {
        label: "Board Rights / Protective Provisions",
        placeholder: "e.g., Board seat, approval rights for major decisions, liquidation preferences",
        description: "Investor rights and protective provisions",
        type: 'textarea'
      }
    }
  }
};

// Helper function to get field config for a specific deal type
export const getDealFieldConfig = (dealType: DealType): DealFieldConfig => {
  return DEAL_FIELD_CONFIGS[dealType];
};

// Helper function to get all specific field keys for a deal type
export const getSpecificFieldKeys = (dealType: DealType): string[] => {
  return Object.keys(DEAL_FIELD_CONFIGS[dealType].specific);
};
